import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { usePacientes } from '../../hooks/usePacientes';
import { ref, get as rtdbGet, push, set as rtdbSet } from '../../services/phpDataAdapter';
import { database } from '../../config/backend';
import { DeepSeekService } from '../../services/deepSeekService';
import { WeeklyMealPlanEditor, emptyWeeklyPlan, DAY_LABELS } from './WeeklyMealPlanEditor';
import { MacroChart } from './MacroChart';
import { MACRO_PRESETS, applyMacroPreset } from '../../utils/macroCalculations';
import { MealPlanHelper } from './MealPlanHelper';
import { calculateNutrition, mapPatientGoalToCalculationGoal, PatientData as NutritionPatientData } from '../../services/nutritionCalculationService';
import { MealPlanSubstitutions, WeeklyMealPlan, DayKey, MealKey, FoodNutrients, MealPlanNutrition } from '../../types/mealPlan';
import { findMealPlanAllergyConflicts } from '../../utils/mealPlanAllergyValidation';
import { EditableMealPlan } from '../../utils/mealPlanData';
import { getDayNutrition, nutrientKey, reconcileFoodNutrients } from '../../utils/mealPlanNutrition';
import { FoodItem } from '../../services/foodDatabaseService';
import { getTrainingMealSchedule } from '../../utils/mealSchedule';
import './Plan.css';

interface CreateMealPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string | null;
  initialPlan?: EditableMealPlan | null;
  onSave: (patientId: string, planData: any) => Promise<void>;
}

export const CreateMealPlanModal: React.FC<CreateMealPlanModalProps> = ({
  isOpen,
  onClose,
  patientId: initialPatientId,
  initialPlan,
  onSave,
}) => {
  const { user } = useAuth();
  const { pacientes } = usePacientes();
  const navigate = useNavigate();
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(initialPatientId);
  const [mode, setMode] = useState<'manual' | 'ai'>('manual');
  const [loading, setLoading] = useState(false);
  const [patientDataLoading, setPatientDataLoading] = useState(false);
  const [patientDataError, setPatientDataError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [patientData, setPatientData] = useState<any>(null);
  const [planData, setPlanData] = useState({
    nome: '',
    objetivo: '',
    observacoes: '',
    dailyCalories: '',
    protein: '',
    carbohydrates: '',
    fats: '',
    trainingTime: '',
  });
  const [weeklyPlan, setWeeklyPlan] = useState<WeeklyMealPlan>(emptyWeeklyPlan);
  const [showWeeklyEditor, setShowWeeklyEditor] = useState(false);
  const [macroHistory, setMacroHistory] = useState<Array<{ protein: number; carbs: number; fats: number; calories: number; createdAt: string }>>([]);
  const [substitutions, setSubstitutions] = useState<MealPlanSubstitutions | null>(null);
  const [nutritionCalculation, setNutritionCalculation] = useState<any>(null);
  const [foodNutrients, setFoodNutrients] = useState<MealPlanNutrition>({});
  const [nutritionDay, setNutritionDay] = useState<DayKey>('monday');
  const dayNutrition = useMemo(() => getDayNutrition(weeklyPlan, foodNutrients, nutritionDay), [weeklyPlan, foodNutrients, nutritionDay]);
  const allergyConflicts = useMemo(
    () => findMealPlanAllergyConflicts(patientData?.allergies, weeklyPlan),
    [patientData?.allergies, weeklyPlan]
  );
  const trainingSchedule = useMemo(
    () => getTrainingMealSchedule(planData.trainingTime),
    [planData.trainingTime]
  );

  useEffect(() => {
    if (!isOpen) return;
    setSelectedPatientId(initialPatientId);
    setPlanData({
      nome: initialPlan?.nome || '', objetivo: initialPlan?.objetivo || '', observacoes: initialPlan?.observacoes || '',
      dailyCalories: initialPlan?.targetCalories?.toString() || '', protein: initialPlan?.protein?.toString() || '',
      carbohydrates: initialPlan?.carbohydrates?.toString() || '', fats: initialPlan?.fats?.toString() || '',
      trainingTime: initialPlan?.trainingTime || '',
    });
    setWeeklyPlan(initialPlan?.weeklyPlan || emptyWeeklyPlan);
    setSubstitutions(initialPlan?.substitutions || null);
    setNutritionCalculation(initialPlan ? { bmr: initialPlan.bmr, tdee: initialPlan.tdee } : null);
    setShowWeeklyEditor(!!initialPlan);
    setFoodNutrients(initialPlan?.foodNutrients || {});
    setNutritionDay('monday');
    setMode('manual');
  }, [initialPatientId, initialPlan, isOpen]);

  useEffect(() => {
    let active = true;
    if (isOpen) {
      setPatientData(null);
      if (selectedPatientId) {
        loadPatientData(() => active);
      } else {
        setPatientDataLoading(false);
      }
      loadMacroHistory();
    }
    return () => { active = false; };
  }, [isOpen, selectedPatientId]);

  const loadMacroHistory = async () => {
    if (!user?.uid) return;
    try {
      const historyRef = ref(database, `users/${user.uid}/macroHistory`);
      const snapshot = await rtdbGet(historyRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        const history = Object.values(data) as Array<{ protein: number; carbs: number; fats: number; calories: number; createdAt: string }>;
        setMacroHistory(history.slice(-10).reverse()); // Últimos 10
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const saveMacroToHistory = async () => {
    if (!user?.uid || !planData.dailyCalories || !planData.protein || !planData.carbohydrates || !planData.fats) return;

    const macroData = {
      protein: parseFloat(planData.protein),
      carbs: parseFloat(planData.carbohydrates),
      fats: parseFloat(planData.fats),
      calories: parseFloat(planData.dailyCalories),
      createdAt: new Date().toISOString()
    };

    try {
      const historyRef = push(ref(database, `users/${user.uid}/macroHistory`));
      await rtdbSet(historyRef, macroData);
      await loadMacroHistory();
    } catch (error) {
      console.error('Erro ao salvar histórico:', error);
    }
  };

  const loadPatientData = async (isCurrent: () => boolean) => {
    if (!selectedPatientId) return;
    setPatientDataLoading(true);
    setPatientDataError(null);
    try {
      const [patientSnap, userSnap] = await Promise.all([
        rtdbGet(ref(database, `patients/${selectedPatientId}`)),
        rtdbGet(ref(database, `users/${selectedPatientId}`)),
      ]);
      if (!isCurrent()) return;

      const patient = patientSnap.exists() ? patientSnap.val() : {};
      const user = userSnap.exists() ? userSnap.val() : {};

      const birthDateValue = patient.birthDate
        || patient.personalData?.birthDate
        || user.birthDate
        || user.personalData?.birthDate;
      let birthDateAge: number | '' = '';
      if (birthDateValue) {
        const dateMatch = String(birthDateValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
        const birthDate = dateMatch
          ? new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]))
          : new Date(birthDateValue);
        if (!Number.isNaN(birthDate.getTime())) {
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          if (today.getMonth() < birthDate.getMonth()
            || (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())) {
            age -= 1;
          }
          if (age >= 0 && age <= 120) birthDateAge = age;
        }
      }
      const storedAge = patient.age ?? user.age;
      const age = storedAge !== '' && storedAge != null && Number.isFinite(Number(storedAge))
        ? Number(storedAge)
        : birthDateAge;

      setPatientData({
        patientId: selectedPatientId,
        name: user.name || patient.name || '',
        age,
        weight: patient.weight || user.weight || '',
        height: patient.height || user.height || '',
        gender: patient.gender || 'male',
        activityLevel: patient.activityLevel || 'moderate',
        goal: mapPatientGoalToCalculationGoal(patient.goals || patient.goal || user.goal),
        medicalConditions: patient.medicalConditions || '',
        allergies: patient.allergies || user.allergies || user.medicalProfile?.allergies || '',
        preferences: patient.preferences || '',
      });
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Erro ao carregar dados do paciente:', error);
      setPatientData(null);
      setPatientDataError('Não foi possível carregar o perfil e validar as alergias. Tente novamente antes de salvar.');
    } finally {
      if (isCurrent()) setPatientDataLoading(false);
    }
  };

  const handleGenerateWithAI = async () => {
    if (!selectedPatientId || patientDataLoading || patientData?.patientId !== selectedPatientId) {
      alert('Aguarde o carregamento dos dados do paciente selecionado antes de gerar o plano.');
      return;
    }

    if (!patientData) {
      alert('Preencha os dados do paciente primeiro.');
      return;
    }

    if (!patientData.name || !patientData.age || !patientData.weight || !patientData.height) {
      alert('Dados do paciente incompletos. Preencha nome, idade, peso e altura.');
      return;
    }

    if (!patientData?.goal) {
      alert('Defina um objetivo reconhecido no cadastro do paciente antes de gerar o plano com IA.');
      return;
    }
    setGenerating(true);
    try {
      // Calcular valores nutricionais antes de chamar a IA
      const nutritionData: NutritionPatientData = {
        weight: parseFloat(patientData.weight) || 70,
        height: parseFloat(patientData.height) || 170,
        age: parseInt(patientData.age) || 30,
        gender: patientData.gender || 'male',
        activityLevel: patientData.activityLevel || 'moderate',
        goal: patientData.goal,
      };

      const nutritionCalc = calculateNutrition(nutritionData);
      setNutritionCalculation(nutritionCalc);

      const deepSeekService = new DeepSeekService();

      // Passar cálculos nutricionais para a IA
      const mealPlan = await deepSeekService.generateMealPlan({
        name: patientData.name,
        age: parseInt(patientData.age) || 30,
        weight: parseFloat(patientData.weight) || 70,
        height: parseFloat(patientData.height) || 170,
        gender: patientData.gender || 'male',
        activityLevel: patientData.activityLevel || 'moderate',
        goal: patientData.goal,
        medicalConditions: patientData.medicalConditions || undefined,
        allergies: patientData.allergies || undefined,
        preferences: patientData.preferences || undefined,
        trainingTime: planData.trainingTime || undefined,
      }, nutritionCalc);

      // Preencher o formulário com os dados gerados
      setPlanData({
        nome: `Plano Alimentar - ${patientData.name}`,
        objetivo: `Objetivo: ${patientData.goal === 'weight_loss' ? 'Emagrecimento' : patientData.goal === 'muscle_gain' ? 'Ganho de Massa' : 'Manutenção'}`,
        observacoes: mealPlan.notes || '',
        dailyCalories: String(mealPlan.dailyCalories),
        protein: String(mealPlan.macronutrients.protein),
        carbohydrates: String(mealPlan.macronutrients.carbohydrates),
        fats: String(mealPlan.macronutrients.fats),
        trainingTime: planData.trainingTime,
      });

      // Preencher o plano semanal
      if (mealPlan.weeklyPlan) {
        setWeeklyPlan(mealPlan.weeklyPlan);
        setShowWeeklyEditor(true);
      }

      // Armazenar substituições se existirem
      if (mealPlan.substitutions) {
        setSubstitutions(mealPlan.substitutions);
      }

      alert('Plano gerado com sucesso! Revise os dados e ajuste se necessário.');
    } catch (error: any) {
      console.error('Erro ao gerar plano:', error);
      alert(`Erro ao gerar plano: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleWeeklyChange = (next: WeeklyMealPlan) => {
    setFoodNutrients(previous => reconcileFoodNutrients(weeklyPlan, next, previous));
    setWeeklyPlan(next);
  };
  const registerNutrients = (day: DayKey, meal: MealKey, index: number, nutrients: FoodNutrients, text: string) => {
    setFoodNutrients(previous => ({ ...previous, [nutrientKey(day, meal, index)]: { ...nutrients, text } }));
  };
  const handleAddFoodToMeal = (food: FoodItem, day: DayKey, meal: MealKey, quantity: number) => {
    if (!Number.isFinite(quantity) || quantity <= 0 || food.servingSize <= 0) return;
    const text = food.name + ' (' + quantity + food.servingUnit + ')';
    const index = weeklyPlan[day][meal].length;
    const next = { ...weeklyPlan, [day]: { ...weeklyPlan[day], [meal]: [...weeklyPlan[day][meal], text] } };
    handleWeeklyChange(next);
    const multiplier = quantity / food.servingSize;
    registerNutrients(day, meal, index, {
      calories: food.nutritionalInfo.calories * multiplier,
      protein: food.nutritionalInfo.protein * multiplier,
      carbs: food.nutritionalInfo.carbs * multiplier,
      fats: food.nutritionalInfo.fats * multiplier,
    }, text);
    setShowWeeklyEditor(true);
  };

  const handleSave = async () => {
    if (!selectedPatientId) {
      alert('Selecione um paciente');
      return;
    }
    if (patientDataLoading || !patientData || patientDataError) {
      alert(patientDataError || 'Aguarde o carregamento do perfil do paciente para validar as alergias.');
      return;
    }
    if (allergyConflicts.length > 0) {
      alert('Remova os itens incompatíveis com as alergias do paciente antes de salvar o plano.');
      return;
    }
    if (!planData.nome) {
      alert('Preencha o nome do plano');
      return;
    }

    setLoading(true);
    try {
      await onSave(selectedPatientId, {
        revision: initialPlan?.revision ?? 0,
        nome: planData.nome,
        objetivo: planData.objetivo,
        observacoes: planData.observacoes,
        trainingTime: planData.trainingTime || undefined,
        targetCalories: planData.dailyCalories ? parseFloat(planData.dailyCalories) : undefined,
        protein: planData.protein ? parseFloat(planData.protein) : undefined,
        carbohydrates: planData.carbohydrates ? parseFloat(planData.carbohydrates) : undefined,
        fats: planData.fats ? parseFloat(planData.fats) : undefined,
        weeklyPlan: weeklyPlan,
        foodNutrients: reconcileFoodNutrients(weeklyPlan, weeklyPlan, foodNutrients),
        hydration: initialPlan?.hydration || '2L/dia',
        durationWeeks: initialPlan?.durationWeeks,
        substitutions: substitutions || undefined,
        bmr: nutritionCalculation?.bmr,
        tdee: nutritionCalculation?.tdee,
      });
      setPlanData({
        nome: '',
        objetivo: '',
        observacoes: '',
        dailyCalories: '',
        protein: '',
        carbohydrates: '',
        fats: '',
        trainingTime: '',
      });
      setWeeklyPlan(emptyWeeklyPlan);
      setSubstitutions(null);
      setNutritionCalculation(null);
      setShowWeeklyEditor(false);
      onClose();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Erro ao salvar plano');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={initialPlan ? 'Editar Plano Alimentar' : 'Criar Novo Plano Alimentar'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading || generating}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={loading || generating || patientDataLoading || !patientData || !!patientDataError || !planData.nome || allergyConflicts.length > 0}>
            {loading ? 'Salvando...' : 'Salvar Plano'}
          </Button>
        </>
      }
    >
      <div className="ns-meal-plan-modal">
        {patientDataLoading && <p role="status">Carregando perfil do paciente e alergias...</p>}
        {patientDataError && <p className="ns-allergy-load-error" role="alert">{patientDataError}</p>}
        {allergyConflicts.length > 0 && (
          <div className="ns-allergy-conflicts" role="alert">
            <strong>O plano contém itens incompatíveis com as alergias cadastradas:</strong>
            <ul>
              {allergyConflicts.map((conflict, index) => (
                <li key={`${conflict.day}-${conflict.meal}-${index}`}>
                  {conflict.day} · {conflict.meal}: “{conflict.item}” (alergia: {conflict.allergen})
                </li>
              ))}
            </ul>
          </div>
        )}
        {!initialPatientId && (
          <label className="ns-field">
            <span>Paciente *</span>
            <select
              className="ns-input"
              value={selectedPatientId || ''}
              disabled={!!initialPlan}
              onChange={(e) => {
                setSelectedPatientId(e.target.value || null);
                setPatientData(null);
              }}
            >
              <option value="">Selecione um paciente</option>
              {pacientes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="ns-mode-selector">
          <button
            type="button"
            className={mode === 'manual' ? 'active' : ''}
            onClick={() => setMode('manual')}
          >
            Manual
          </button>
          <button
            type="button"
            className={mode === 'ai' ? 'active' : ''}
            onClick={() => setMode('ai')}
            disabled={!selectedPatientId}
          >
            Com Auxílio de IA (Opcional)
          </button>
        </div>

        {mode === 'ai' && (
          <div className="ns-ai-section">
            <div className="ns-ai-info">
              <p>
                <strong>Geração com IA:</strong> Use uma API de inteligência artificial para gerar um plano
                personalizado baseado nos dados do paciente. Configure a chave DeepSeek no arquivo privado de configuração da Hostinger.
              </p>
            </div>

            <div style={{ padding: '12px', background: 'var(--page-surface-hover)', borderRadius: '8px', marginBottom: '16px', border: '1px solid var(--page-border)' }}>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--page-text-primary)' }}>
                💡 <strong>Dica:</strong> Configure a DeepSeek no servidor em{' '}
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate('/configuracoes');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#0284c7',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: 600,
                    padding: 0,
                    fontSize: '13px',
                  }}
                >
                  Configurações do sistema
                </button>
                . A geração usa a chave privada DeepSeek configurada no servidor.
              </p>
            </div>

            {patientData && (
              <div className="ns-patient-preview">
                <h4>Dados do Paciente (para IA):</h4>
                <ul>
                  <li>Nome: {patientData.name || 'Não informado'}</li>
                  <li>Idade: {patientData.age || 'Não informado'}</li>
                  <li>Peso: {patientData.weight || 'Não informado'} kg</li>
                  <li>Altura: {patientData.height || 'Não informado'} cm</li>
                  <li>Objetivo: {patientData.goal || 'Não informado'}</li>
                  {planData.trainingTime && <li>Treino: {planData.trainingTime} · pré às {trainingSchedule?.preWorkoutTime} · pós às {trainingSchedule?.postWorkoutTime}</li>}
                  {patientData.preferences && (
                    <li><strong style={{ color: '#10b981' }}>✅ Preferências:</strong> {patientData.preferences}</li>
                  )}
                  {patientData.allergies && (
                    <li><strong style={{ color: '#ef4444' }}>❌ Alergias:</strong> {patientData.allergies}</li>
                  )}
                  {patientData.medicalConditions && (
                    <li><strong style={{ color: '#f59e0b' }}>⚠️ Condições:</strong> {patientData.medicalConditions}</li>
                  )}
                </ul>
              </div>
            )}

            <Button
              variant="secondary"
              onClick={handleGenerateWithAI}
              disabled={generating || patientDataLoading || !!patientDataError || !patientData || patientData.patientId !== selectedPatientId}
            >
              {generating ? 'Gerando plano...' : 'Gerar Plano com IA'}
            </Button>
          </div>
        )}

        <div className="ns-form">
          <label className="ns-field">
            <span>Nome do plano *</span>
            <input
              className="ns-input"
              placeholder="Ex: Plano de Emagrecimento - Semana 1"
              value={planData.nome}
              onChange={(e) => setPlanData({ ...planData, nome: e.target.value })}
            />
          </label>

          <label className="ns-field">
            <span>Objetivo principal</span>
            <input
              className="ns-input"
              placeholder="Ex: Perda de 3kg em 6 semanas"
              value={planData.objetivo}
              onChange={(e) => setPlanData({ ...planData, objetivo: e.target.value })}
            />
          </label>

          <label className="ns-field">
            <span>Horário habitual do treino</span>
            <input
              type="time"
              className="ns-input"
              value={planData.trainingTime}
              onChange={(e) => setPlanData({ ...planData, trainingTime: e.target.value })}
              aria-describedby="training-time-hint"
            />
            <span id="training-time-hint" className="ns-hint">
              Opcional. Ajuda a posicionar o pré e o pós-treino e os demais horários das refeições.
            </span>
          </label>
          {trainingSchedule && (
            <div className="ns-ai-info" role="status">
              <strong>Horários sugeridos:</strong> pré-treino às {trainingSchedule.preWorkoutTime}
              {' · '}pós-treino às {trainingSchedule.postWorkoutTime}.
              {' '}No editor semanal, os horários aparecem ao lado de cada refeição; revise conforme a rotina do paciente.
            </div>
          )}

          {/* Macro Presets */}
          <div className="ns-macro-presets">
            <label>
              Presets de Macros
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {MACRO_PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() => {
                    if (!planData.dailyCalories) {
                      alert('Preencha as calorias diárias primeiro');
                      return;
                    }
                    const macros = applyMacroPreset(preset, parseFloat(planData.dailyCalories));
                    setPlanData({
                      ...planData,
                      protein: String(macros.protein),
                      carbohydrates: String(macros.carbs),
                      fats: String(macros.fats)
                    });
                  }}
                  className="ns-macro-preset"
                  title={preset.description}
                >
                  {preset.name} ({preset.protein}/{preset.carbs}/{preset.fats})
                </button>
              ))}
            </div>
            <p className="ns-macro-presets-hint">
              Clique em um preset para aplicar a distribuição de macros (P/C/G)
            </p>
          </div>

          {/* Helper Button */}
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
            <MealPlanHelper
              onAddMeal={meal => {
                const text = meal.name + ': ' + meal.foods.map(food => food.foodName + ' (' + food.quantity + food.unit + ')').join(', ');
                const index = weeklyPlan[nutritionDay].breakfast.length;
                handleWeeklyChange({ ...weeklyPlan, [nutritionDay]: { ...weeklyPlan[nutritionDay], breakfast: [...weeklyPlan[nutritionDay].breakfast, text] } });
                registerNutrients(nutritionDay, 'breakfast', index, { calories: meal.totalCalories, protein: meal.totalProtein, carbs: meal.totalCarbs, fats: meal.totalFats }, text);
                setShowWeeklyEditor(true);
              }}
              onAddFood={(food, quantity) => handleAddFoodToMeal(food, nutritionDay, 'breakfast', quantity)}
              onAddFoodToMeal={handleAddFoodToMeal}
            />
          </div>
          {planData.dailyCalories && planData.protein && planData.carbohydrates && planData.fats && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
                <div style={{ flex: '1 1 260px' }}>
                  <MacroChart
                    proteinGrams={parseFloat(planData.protein) || 0}
                    carbsGrams={parseFloat(planData.carbohydrates) || 0}
                    fatsGrams={parseFloat(planData.fats) || 0}
                    totalCalories={parseFloat(planData.dailyCalories) || 0}
                  />
                </div>
                <div style={{ flex: '1 1 260px', background: 'var(--page-surface-hover)', borderRadius: 12, padding: 16 }}>
                  <h4 style={{ marginTop: 0 }}>Nutrientes de {DAY_LABELS[nutritionDay]}</h4>
                  {dayNutrition.knownItems > 0 ? <>
                    <p>{Math.round(dayNutrition.totals.calories)} kcal · P: {dayNutrition.totals.protein.toFixed(1)}g · C: {dayNutrition.totals.carbs.toFixed(1)}g · G: {dayNutrition.totals.fats.toFixed(1)}g</p>
                    <p>{dayNutrition.knownItems} de {dayNutrition.items} itens com composição.</p>
                    {!dayNutrition.complete && <p role="status">Subtotal parcial. Itens digitados livremente ainda precisam de composição nutricional.</p>}
                  </> : <p>{dayNutrition.items ? 'Composição nutricional ainda não cadastrada para os itens deste dia.' : 'Nenhum alimento neste dia.'}</p>}
                  <p className="ns-hint">Selecione alimentos pela busca para calcular as porções. Defina as metas nos campos do plano.</p>
                </div>
              </div>

              {/* Percentages Panel (Meta) */}
              <div style={{
                background: 'var(--page-surface)',
                border: '2px solid var(--page-border)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, fontSize: '14px', color: 'var(--page-text-primary)' }}>
                    Distribuição Percentual de Macronutrientes
                  </h4>
                  <Button
                    variant="secondary"
                    onClick={saveMacroToHistory}
                    style={{ fontSize: '11px', padding: '4px 8px' }}
                  >
                    💾 Salvar no Histórico
                  </Button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  <div style={{ textAlign: 'center', padding: '12px', background: 'var(--page-surface-hover)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#0ea5e9' }}>
                      {parseFloat(planData.dailyCalories) > 0 ? ((parseFloat(planData.protein) * 4 / parseFloat(planData.dailyCalories)) * 100).toFixed(1) : '0'}%
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginTop: '4px' }}>Proteínas</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '12px', background: 'var(--page-surface-hover)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>
                      {parseFloat(planData.dailyCalories) > 0 ? ((parseFloat(planData.carbohydrates) * 4 / parseFloat(planData.dailyCalories)) * 100).toFixed(1) : '0'}%
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginTop: '4px' }}>Carboidratos</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '12px', background: 'var(--page-surface-hover)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}>
                      {parseFloat(planData.dailyCalories) > 0 ? ((parseFloat(planData.fats) * 9 / parseFloat(planData.dailyCalories)) * 100).toFixed(1) : '0'}%
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginTop: '4px' }}>Gorduras</div>
                  </div>
                </div>

                {/* Macro History */}
                {macroHistory.length > 0 && (
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--page-border)' }}>
                    <h5 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--page-text-muted)', marginBottom: '8px' }}>
                      Histórico de Distribuições
                    </h5>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '120px', overflowY: 'auto' }}>
                      {macroHistory.map((hist, index) => {
                        const pPct = ((hist.protein * 4 / hist.calories) * 100).toFixed(1);
                        const cPct = ((hist.carbs * 4 / hist.calories) * 100).toFixed(1);
                        const fPct = ((hist.fats * 9 / hist.calories) * 100).toFixed(1);
                        return (
                          <button
                            key={index}
                            type="button"
                            onClick={() => {
                              setPlanData({
                                ...planData,
                                dailyCalories: String(hist.calories),
                                protein: String(hist.protein),
                                carbohydrates: String(hist.carbs),
                                fats: String(hist.fats)
                              });
                            }}
                            style={{
                              padding: '8px',
                              background: 'var(--page-surface-hover)',
                              border: '1px solid var(--page-border)',
                              borderRadius: '6px',
                              fontSize: '11px',
                              textAlign: 'left',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'var(--page-surface-hover)';
                              e.currentTarget.style.borderColor = '#0ea5e9';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'var(--page-surface-hover)';
                              e.currentTarget.style.borderColor = 'var(--page-border)';
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>P: {pPct}% | C: {cPct}% | G: {fPct}%</span>
                              <span style={{ color: 'var(--page-text-muted)', fontSize: '10px' }}>
                                {new Date(hist.createdAt).toLocaleDateString('pt-BR')}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="ns-macro-grid">
            <label className="ns-field">
              <span>Calorias diárias</span>
              <input
                type="number"
                className="ns-input"
                placeholder="Ex: 2000"
                value={planData.dailyCalories}
                onChange={(e) => setPlanData({ ...planData, dailyCalories: e.target.value })}
              />
            </label>

            <label className="ns-field">
              <span>Proteínas (g)</span>
              <input
                type="number"
                className="ns-input"
                placeholder="Ex: 150"
                value={planData.protein}
                onChange={(e) => setPlanData({ ...planData, protein: e.target.value })}
              />
            </label>

            <label className="ns-field">
              <span>Carboidratos (g)</span>
              <input
                type="number"
                className="ns-input"
                placeholder="Ex: 200"
                value={planData.carbohydrates}
                onChange={(e) => setPlanData({ ...planData, carbohydrates: e.target.value })}
              />
            </label>

            <label className="ns-field">
              <span>Gorduras (g)</span>
              <input
                type="number"
                className="ns-input"
                placeholder="Ex: 67"
                value={planData.fats}
                onChange={(e) => setPlanData({ ...planData, fats: e.target.value })}
              />
            </label>
          </div>

          <label className="ns-field">
            <span>Observações para o paciente</span>
            <textarea
              className="ns-textarea"
              rows={4}
              placeholder="Recomendações, restrições, orientações gerais..."
              value={planData.observacoes}
              onChange={(e) => setPlanData({ ...planData, observacoes: e.target.value })}
            />
          </label>

          <div className="ns-weekly-editor-toggle">
            <button
              type="button"
              className={showWeeklyEditor ? 'active' : ''}
              onClick={() => setShowWeeklyEditor(!showWeeklyEditor)}
            >
              {showWeeklyEditor ? '▼' : '▶'} Editor de Refeições Semanal
            </button>
            <p className="ns-hint">
              {showWeeklyEditor
                ? 'Edite as refeições de cada dia da semana abaixo'
                : 'Clique para abrir o editor completo de refeições por dia da semana'}
            </p>
          </div>

          {showWeeklyEditor && (
            <div className="ns-weekly-editor-container">
              <WeeklyMealPlanEditor
                weeklyPlan={weeklyPlan}
                trainingTime={planData.trainingTime}
                onChange={handleWeeklyChange}
                substitutions={substitutions}
                onDayChange={setNutritionDay}
                onNutrientAdd={registerNutrients}
                onSubstitutionSelect={(day, mealType, optionId) => {
                  if (!substitutions) return;

                  const mealSub = substitutions.mealSubstitutions?.find(
                    s => s.day === day && s.mealType === mealType
                  );

                  if (mealSub) {
                    const selectedOption = mealSub.options.find(opt => opt.id === optionId);
                    if (selectedOption) {
                      const updatedPlan = { ...weeklyPlan };
                      const dayMeals = { ...updatedPlan[day] };
                      dayMeals[mealType] = selectedOption.foods;
                      updatedPlan[day] = dayMeals;
                      setWeeklyPlan(updatedPlan);
                    }
                  }
                }}
              />
            </div>
          )}

          {/* Seção de Substituições */}
          {substitutions && ((substitutions.mealSubstitutions?.length || 0) > 0 || (substitutions.foodSubstitutions?.length || 0) > 0) && (
            <div style={{ marginTop: '24px', padding: '16px', background: 'var(--page-surface-hover)', borderRadius: '8px', border: '1px solid var(--page-border)' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: 'var(--page-text-primary)' }}>
                🔄 Substituições Disponíveis
              </h3>

              {/* Substituições de Refeições */}
              {(substitutions.mealSubstitutions?.length || 0) > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--page-text-muted)', marginBottom: '12px' }}>
                    Opções de Refeições
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {substitutions.mealSubstitutions?.map((sub, idx) => (
                      <div key={idx} style={{ padding: '12px', background: 'var(--page-surface)', borderRadius: '6px', border: '1px solid var(--page-border)' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--page-text-primary)', marginBottom: '8px' }}>
                          {sub.day ? `${sub.day.charAt(0).toUpperCase() + sub.day.slice(1)} - ` : ''}
                          {sub.mealType === 'breakfast' ? 'Café da Manhã' :
                            sub.mealType === 'morningSnack' ? 'Lanche da Manhã' :
                              sub.mealType === 'lunch' ? 'Almoço' :
                                sub.mealType === 'afternoonSnack' ? 'Lanche da Tarde' :
                                  sub.mealType === 'dinner' ? 'Jantar' : 'Ceia'}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {sub.options.map((option, optIdx) => (
                            <div key={option.id} style={{ padding: '8px', background: 'var(--page-surface-hover)', borderRadius: '4px' }}>
                              <div style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginBottom: '4px' }}>
                                Opção {optIdx + 1}: {option.foods.join(', ')}
                              </div>
                              <div style={{ fontSize: '11px', color: 'var(--page-text-muted)' }}>
                                {option.calories} kcal | P: {option.macros.protein}g | C: {option.macros.carbohydrates}g | G: {option.macros.fats}g
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Substituições de Alimentos */}
              {(substitutions.foodSubstitutions?.length || 0) > 0 && (
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--page-text-muted)', marginBottom: '12px' }}>
                    Alternativas de Alimentos
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {substitutions.foodSubstitutions?.map((sub, idx) => (
                      <div key={idx} style={{ padding: '12px', background: 'var(--page-surface)', borderRadius: '6px', border: '1px solid var(--page-border)' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--page-text-primary)', marginBottom: '8px' }}>
                          {sub.originalFood}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginBottom: '8px' }}>
                          Pode ser substituído por:
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {sub.alternatives.map((alt, altIdx) => (
                            <div key={altIdx} style={{ padding: '6px', background: 'var(--page-surface-hover)', borderRadius: '4px', fontSize: '12px' }}>
                              • {alt.food} ({alt.quantity})
                              {alt.equivalentMacros && <span style={{ color: '#10b981', marginLeft: '8px' }}>✓ Equivalente</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
